import type { Summary } from './types';
import { getChartRuntime } from './chartRuntime';
import { BAR_PALETTE } from './palettes';
import { buildBarData } from './utils';

function ellipsize(s: string, max = 18) {
  const str = String(s);
  return str.length > max ? str.slice(0, max - 1) + '…' : str;
}

export function renderBarChart(summary: Summary, orientation: 'vertical' | 'horizontal'): HTMLElement {
  const container = document.createElement('div');
  container.className = 'chart chart--bar';

  const data = buildBarData(summary);
  if (!data.length) {
    return container;
  }

  const canvas = document.createElement('canvas');
  container.appendChild(canvas);

  const colors = data.map((_, index) => BAR_PALETTE[index % BAR_PALETTE.length]);
  const ChartRuntime = getChartRuntime();

  const isHorizontal = orientation === 'horizontal';
  const indexAxis = isHorizontal ? 'y' : 'x';

  const chart = new ChartRuntime(canvas, {
    type: 'bar',
    data: {
      labels: data.map((entry) => entry.label),
      datasets: [
        {
          label: 'Responses',
          data: data.map((entry) => entry.count),
          backgroundColor: colors,
          borderRadius: 6,
          borderWidth: 0,
          barPercentage: 0.995,
          categoryPercentage: 0.995,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 600 },
      indexAxis,
      scales: {
        x: {
          ticks: {
            color: '#475569',
            font: { size: 12 },
            autoSkip: isHorizontal,
            // Only truncate labels when X is the category axis (vertical bars)
            callback: !isHorizontal
              ? (value: string | number) => {
                  const labels = chart.data.labels as Array<string | number> | undefined;
                  const full =
                    typeof value === 'number'
                      ? String(labels?.[value] ?? '')
                      : String(value);
                  return ellipsize(full, 12);
                }
              : undefined,
          },
          grid: {
            color: isHorizontal ? 'rgba(148, 163, 184, 0.18)' : 'transparent',
            drawBorder: false,
          },
        },

        y: {
          ticks: {
            color: '#475569',
            font: { size: 12 },
            autoSkip: !isHorizontal,
            // Only truncate labels when Y is the category axis (horizontal bars)
            callback: isHorizontal
              ? (value: string | number) => {
                  const labels = chart.data.labels as Array<string | number> | undefined;
                  const full =
                    typeof value === 'number'
                      ? String(labels?.[value] ?? '')
                      : String(value);
                  return ellipsize(full, 22);
                }
              : undefined,
          },
          grid: {
            color: isHorizontal ? 'transparent' : 'rgba(148, 163, 184, 0.18)',
            drawBorder: false,
          },

          // Reserve a minimum gutter on the left for labels in horizontal mode
          ...(isHorizontal && {
            afterFit: (scale: any) => {
              scale.width = Math.max(scale.width, 110);
            },
          }),
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            // Full label in tooltip title (tap/hover)
            title(items: any[]) {
              if (!items?.length) return '';
              const i = items[0].dataIndex as number;
              const labels = chart.data.labels as Array<string | number> | undefined;
              return String(labels?.[i] ?? '');
            },
            label(context: any) {
              const count = context.raw as number;
              const total = summary.n || 0;
              const pct = total ? `${((count / total) * 100).toFixed(1)}%` : '0%';
              return `${count} responses (${pct})`;
            },
          },
        },
      },
    },
  });

  (globalThis as any).__activeCharts = (globalThis as any).__activeCharts || [];
  (globalThis as any).__activeCharts.push(chart);

  return container;
}